/**
 * Shared visual language for the RDS migration layouts.
 *
 * One design system. The single rule it obeys: a piece of data is ALWAYS
 * tagged with the word SUPABASE or the word RDS, never colour alone. Colour
 * is a second channel that says STATE, not identity:
 *
 *   GREEN  = this system is serving data right now (enabled / in use)
 *   RED    = this system is not serving (disabled, vetoed, paused or down)
 *   both green = both systems are in use at the same time
 *   orange = a warning that needs its sentence (a switch on but not working)
 *   grey   = neutral; nothing to say
 */
import React from "react";
import {
  Box, HStack, VStack, Text, Badge, Progress, Flex, Tooltip,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import {
  CheckIcon, WarningIcon, InfoIcon, LockIcon, SmallCloseIcon,
} from "@chakra-ui/icons";
import {
  SOURCE, SOURCE_LABEL, SOURCE_TONE, ON_TONE, OFF_TONE, shortAge,
} from "./rdsViewModel";

export { SOURCE, SOURCE_LABEL, SOURCE_TONE, ON_TONE, OFF_TONE, shortAge };

/** A small solid dot. `tone` is a Chakra colour scheme. */
export function Dot({ tone = "gray", size = "8px", boxShadow }) {
  return (
    <Box
      w={size}
      h={size}
      borderRadius="full"
      bg={`${tone}.400`}
      boxShadow={boxShadow ? `0 0 0 3px var(--chakra-colors-${tone.replace(".", "-")}-100)` : undefined}
      flexShrink={0}
      aria-hidden="true"
    />
  );
}

/**
 * The source tag. The whole point of the redesign: every row that can be
 * served from either side carries this, with the word spelled out.
 */
export function SourceChip({ source = SOURCE.NONE, size = "sm", label, variant = "solid" }) {
  const tone = SOURCE_TONE[source] || "gray";
  const text = label || SOURCE_LABEL[source] || "NO SOURCE";
  return (
    <Badge
      colorScheme={tone}
      variant={variant}
      fontSize={size === "xs" ? "9px" : "10px"}
      letterSpacing="0.06em"
      px={2}
      py={size === "xs" ? "1px" : "2px"}
      borderRadius="md"
      whiteSpace="nowrap"
    >
      {text}
    </Badge>
  );
}

/** A neutral state pill (ON / OFF / BLOCKED / …). */
export function StatePill({ tone = "gray", children, size = "sm" }) {
  return (
    <Badge
      colorScheme={tone}
      variant="subtle"
      fontSize={size === "xs" ? "9px" : "10px"}
      letterSpacing="0.04em"
      px={2}
      py="1px"
      borderRadius="full"
      whiteSpace="nowrap"
    >
      {children}
    </Badge>
  );
}

/**
 * The two-system bar for one row: SUPABASE on the left, RDS on the right.
 * Each half is green when that system serves this row right now and red
 * when it does not, and each half spells out its name and state, so the
 * bar reads without its colour. Fed by `row.systems` (the view model's own
 * verdict), never recomputed here.
 */
export function SplitBar({ row, height = "18px" }) {
  const sys = row?.systems;
  if (!sys?.supabase || !sys?.rds) return null;
  const id = row.bucket || row.table || "row";
  const half = (s) => (
    <Flex
      key={s.key}
      flex="1"
      bg={`${s.tone}.500`}
      align="center"
      justify="center"
      title={`${s.name} · ${s.state}`}
      data-testid={`split-${id}-${s.key}`}
      data-on={s.on ? "true" : "false"}
    >
      <HStack spacing={1} px={1} minW={0}>
        {s.on ? <CheckIcon boxSize="8px" color="white" /> : <SmallCloseIcon boxSize="10px" color="white" />}
        <Text fontSize="9px" fontWeight="bold" color="white" letterSpacing="0.08em" noOfLines={1}>
          {s.name} · {s.state}
        </Text>
      </HStack>
    </Flex>
  );
  return (
    <Flex w="100%" h={height} borderRadius="md" overflow="hidden" gap="2px" bg="white">
      {half(sys.supabase)}
      {half(sys.rds)}
    </Flex>
  );
}

/**
 * The two-word answer: [✓ SUPABASE · IN USE] [✕ RDS · NOT IN USE]. Each chip
 * is green when that system serves right now and red when it does not;
 * both are green when both serve. `systems` is vm.systems (the whole
 * dashboard) or a row's `systems` (one table / bucket).
 */
export function SystemPair({ systems, size = "sm", testidPrefix = "rds-pair" }) {
  if (!systems?.supabase || !systems?.rds) return null;
  const xs = size === "xs";
  const chip = (s) => (
    <HStack
      key={s.key}
      spacing={1}
      px={xs ? 1.5 : 2}
      py={xs ? "1px" : "3px"}
      borderRadius="md"
      bg={`${s.tone}.500`}
      color="white"
      title={s.why || undefined}
      data-testid={`${testidPrefix}-${s.key}`}
      data-on={s.on ? "true" : "false"}
    >
      {s.on ? <CheckIcon boxSize={xs ? "8px" : "9px"} /> : <SmallCloseIcon boxSize={xs ? "10px" : "12px"} />}
      <Text
        fontSize={xs ? "9px" : "10px"}
        fontWeight="bold"
        letterSpacing="0.06em"
        whiteSpace="nowrap"
        lineHeight="1.4"
      >
        {s.name} · {s.state}
      </Text>
    </HStack>
  );
  return (
    <HStack spacing={1.5} flexWrap="wrap">
      {chip(systems.supabase)}
      {chip(systems.rds)}
    </HStack>
  );
}

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.55); }
  70%  { box-shadow: 0 0 0 7px rgba(72, 187, 120, 0); }
  100% { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0); }
`;

/** A pulsing dot while the view is refreshing itself; still and grey when not. */
export function LiveDot({ on = true, size = "9px" }) {
  return (
    <Box
      w={size}
      h={size}
      borderRadius="full"
      bg={on ? `${ON_TONE}.400` : "gray.300"}
      animation={on ? `${pulse} 1.6s ease-out infinite` : undefined}
      flexShrink={0}
      aria-hidden="true"
      data-testid="rds-live-dot"
      data-on={on ? "true" : "false"}
    />
  );
}

/** A clock that re-renders its caller every `intervalMs`, for "updated 3 s ago". */
export function useNow(intervalMs = 1000) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * A strip of thin bars: events per time slot, oldest left, newest right.
 * One hue for magnitude; an empty slot is a faint baseline mark, never a
 * gap; every bar is titled with its count and age so the value can be read.
 */
export function ActivityBars({
  slots, tone = ON_TONE, height = 28, testid, label = "RDS events", field = "events", unit = "events",
}) {
  const list = Array.isArray(slots) ? slots : [];
  if (!list.length) return null;
  const max = Math.max(1, ...list.map((s) => s[field] || 0));
  return (
    <HStack
      spacing="2px"
      align="flex-end"
      h={`${height}px`}
      w="100%"
      role="img"
      aria-label={`${label}, ${list.length} slots, oldest left`}
      data-testid={testid}
    >
      {list.map((s) => {
        const n = s[field] || 0;
        const h = n ? Math.max(3, Math.round((n / max) * height)) : 2;
        return (
          <Box
            key={s.t}
            flex="1"
            minW="2px"
            h={`${h}px`}
            bg={n ? `${tone}.500` : "gray.200"}
            borderTopRadius="2px"
            title={`${n} ${unit} · ${shortAge(s.agoSec)}`}
          />
        );
      })}
    </HStack>
  );
}

/** One usage line: label left, value right, an optional hint under the label. */
export function UsageRow({ label, value, hint, tone = "gray", testid }) {
  return (
    <HStack justify="space-between" align="flex-start" spacing={3} data-testid={testid}>
      <Box minW={0}>
        <Text fontSize="xs" color="gray.600" noOfLines={1}>{label}</Text>
        {hint && <Text fontSize="10px" color="gray.400" noOfLines={2}>{hint}</Text>}
      </Box>
      <Text
        fontSize="xs"
        fontWeight="bold"
        color={tone === "gray" ? "gray.800" : `${tone}.700`}
        textAlign="right"
        whiteSpace="nowrap"
      >
        {value}
      </Text>
    </HStack>
  );
}

/**
 * A titled card. `tone` tints the left edge; `testid` is stable so the
 * layouts can be asserted on.
 */
export function PanelCard({
  title, subtitle, right, children, tone = "gray", testid, p = 4, dense = false,
}) {
  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
      borderLeftWidth={tone === "gray" ? "1px" : "4px"}
      borderLeftColor={tone === "gray" ? "gray.200" : `${tone}.400`}
      boxShadow="0 1px 2px rgba(16,24,40,0.04)"
      p={p}
      data-testid={testid}
    >
      {(title || right) && (
        <HStack justify="space-between" align="flex-start" spacing={3} mb={dense ? 2 : 3}>
          <Box minW={0}>
            {title && (
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="gray.500"
                letterSpacing="0.08em"
                textTransform="uppercase"
              >
                {title}
              </Text>
            )}
            {subtitle && (
              <Text fontSize="xs" color="gray.500" mt={0.5}>
                {subtitle}
              </Text>
            )}
          </Box>
          {right && <Box flexShrink={0}>{right}</Box>}
        </HStack>
      )}
      {children}
    </Box>
  );
}

/** A small uppercase heading with a rule under it. */
export function SectionLabel({ children, right, mb = 2 }) {
  return (
    <HStack justify="space-between" align="center" mb={mb} spacing={3}>
      <Text
        fontSize="10px"
        fontWeight="bold"
        color="gray.400"
        letterSpacing="0.12em"
        textTransform="uppercase"
      >
        {children}
      </Text>
      {right}
    </HStack>
  );
}

/** One number with a label under it. */
export function Kpi({ label, value, tone = "gray", hint }) {
  return (
    <Box
      bg={`${tone}.50`}
      borderWidth="1px"
      borderColor={`${tone}.100`}
      borderRadius="lg"
      px={3}
      py={2}
      minW={0}
    >
      <Text fontSize="10px" fontWeight="bold" color={`${tone}.700`} letterSpacing="0.06em" textTransform="uppercase" noOfLines={1}>
        {label}
      </Text>
      <Text fontSize="xl" fontWeight="bold" color={`${tone}.800`} lineHeight="1.2" mt={0.5} noOfLines={1}>
        {value}
      </Text>
      {hint && (
        <Text fontSize="xs" color="gray.500" mt={0.5} noOfLines={2}>
          {hint}
        </Text>
      )}
    </Box>
  );
}

/** A labelled progress meter with its own caption. */
export function Meter({ pct, tone = "blue", caption, height = "6px" }) {
  return (
    <Box>
      {caption && (
        <HStack justify="space-between" mb={1}>
          <Text fontSize="xs" color="gray.500">{caption}</Text>
          <Text fontSize="xs" fontWeight="bold" color={`${tone}.700`}>{pct}%</Text>
        </HStack>
      )}
      <Progress value={pct} colorScheme={tone} size="sm" borderRadius="full" bg="gray.100" />
    </Box>
  );
}

/** One precondition, as a check / cross / dash. */
export function CheckRow({ ok, label, detail, tone }) {
  const scheme = tone || (ok === true ? "green" : ok === false ? "red" : "gray");
  const Icon = ok === true ? CheckIcon : ok === false ? WarningIcon : InfoIcon;
  return (
    <HStack spacing={2} align="flex-start">
      <Box
        w="18px"
        h="18px"
        borderRadius="full"
        bg={`${scheme}.100`}
        color={`${scheme}.600`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        mt="1px"
      >
        <Icon boxSize="10px" />
      </Box>
      <Box minW={0}>
        <Text fontSize="xs" fontWeight="bold" color="gray.700" noOfLines={2}>
          {label}
        </Text>
        {detail && (
          <Text fontSize="xs" color="gray.500" noOfLines={3}>
            {detail}
          </Text>
        )}
      </Box>
    </HStack>
  );
}

/** A pill that says who serves the data, for a table or a bucket row. */
export function ServingBadge({ row }) {
  if (row?.kind === "storage-bucket") {
    return <StatePill tone={row.tone}>{row.badge}</StatePill>;
  }
  return <StatePill tone={row?.tone}>{row?.badge}</StatePill>;
}

/** A locked row: RDS itself holds the switch. */
export function ForcedBadge({ row }) {
  if (!row?.forced) return null;
  return (
    <Tooltip label="RDS holds this switch while it is independent — turn it off from RDS's own admin page">
      {/* The Box is the ref-able child the Tooltip needs: a function
          component cannot carry the ref, and the explanation would never
          reach the operator. */}
      <Box display="inline-flex">
        <StatePill tone={row.forced === "on" ? "purple" : "red"}>
          <HStack spacing={1}>
            <LockIcon boxSize="9px" />
            <Text as="span">{row.forced === "on" ? "FORCED ON" : "FORCED OFF"}</Text>
          </HStack>
        </StatePill>
      </Box>
    </Tooltip>
  );
}

/** A legend row for the source colours. */
export function SourceLegend({ items }) {
  return (
    <HStack spacing={4} flexWrap="wrap">
      {(items || []).map((it) => (
        <HStack key={it.label} spacing={1.5}>
          <Dot tone={it.tone} size="7px" />
          <Text fontSize="xs" color="gray.600">{it.label}</Text>
        </HStack>
      ))}
    </HStack>
  );
}

/**
 * A collapsible block whose children stay in the DOM (only hidden), so a
 * collapsed section still carries its buttons and text.
 */
export function Disclosure({ title, hint, badge, tone = "gray", defaultOpen = false, testid, children, right }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
      bg="white"
      overflow="hidden"
      data-testid={testid}
    >
      <HStack
        as="button"
        type="button"
        w="100%"
        px={4}
        py={3}
        justify="space-between"
        onClick={() => setOpen((v) => !v)}
        _hover={{ bg: "gray.50" }}
        textAlign="left"
        aria-expanded={open}
      >
        <HStack spacing={3} minW={0}>
          <Text fontSize="xs" color="gray.400" w="12px">{open ? "▾" : "▸"}</Text>
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="bold" color="gray.700">{title}</Text>
            {hint && <Text fontSize="xs" color="gray.500" noOfLines={1}>{hint}</Text>}
          </Box>
          {badge && <StatePill tone={tone}>{badge}</StatePill>}
        </HStack>
        {right}
      </HStack>
      <Box display={open ? "block" : "none"} px={4} pb={4} pt={1}>
        <VStack align="stretch" spacing={3}>{children}</VStack>
      </Box>
    </Box>
  );
}

/** An empty-state line, so a section never renders as a blank box. */
export function EmptyNote({ children }) {
  return (
    <Text fontSize="xs" color="gray.400" fontStyle="italic">
      {children}
    </Text>
  );
}
